<template>
  <DashboardCard
    :loading="fetching"
    minSkeletonHeight="144px"
  >
    <!-- HEADER -->
    <div slot="header">
      {{ $t("synchronization.dashboard_card_title") }}
    </div>

    <!-- BODY -->
    <div
      slot="body"
      class="body"
    >
      <!-- DONUT -->
      <div class="body__apex-wrapper">
        <VueApexCharts
          type="donut"
          height="150"
          :options="chartOptions"
          :series="series"
        />
      </div>

      <!-- LABELS -->

      <div class="body__text-wrapper">
        <div class="text">
          <div class="text__label text__label--bold">
            Offer ID {{ $t("synchronization.all_in_file") }}
          </div>
          <div class="text__value">{{ offersAllCount }}</div>
        </div>

        <div class="text">
          <div class="text__label text__label--dics text__label--dics-green">
            {{ $t("synchronization.sells") }}
          </div>
          <div class="text__value">{{ offersIsSellingCount }}</div>
        </div>

        <div class="text">
          <div class="text__label text__label--dics text__label--dics-orange">
            {{ $t("synchronization.not_sells") }}
          </div>
          <div class="text__value">{{ offersNotSellingCount }}</div>
        </div>

        <div class="text">
          <div class="text__label text__label--dics text__label--dics-red">
            {{ $t("synchronization.xml_error") }}
          </div>
          <div class="text__value">
            {{ offersWithErrorsCount }}
            <a-dropdown :trigger="['click']">
              <a-spin
                :spinning="
                  ![TaskStatus.FAILURE, TaskStatus.SUCCESS, null].includes(xmlWithErrors.status)
                "
              >
                <a-icon
                  slot="indicator"
                  type="loading"
                  style="font-size: 24px; margin: -12px"
                  spin
                />
                <a-icon
                  type="download"
                  style="font-size: 16px; color: #1890ff; cursor: pointer"
                />
              </a-spin>

              <a-menu slot="overlay">
                <a-menu-item
                  key="1"
                  @click="handleDownloadXmlWithErrors('csv')"
                >
                  CSV
                </a-menu-item>
                <a-menu-item
                  key="2"
                  @click="handleDownloadXmlWithErrors('xlsx')"
                >
                  XLSX
                </a-menu-item>
              </a-menu>
            </a-dropdown>
          </div>
        </div>

        <div class="text">
          <div class="text__label text__label--dics text__label--dics-blue">
            {{ $t("synchronization.need_action") }}
          </div>
          <div class="text__value">{{ offersIsNeedActionCount }}</div>
        </div>
      </div>
    </div>
  </DashboardCard>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue"
import VueApexCharts from "vue-apexcharts"

import DashboardCard from "@/components/Common/DashboardCard"
import { useDownload } from "@/composables/useDownload"
import { TaskState, useTask } from "@/composables/useTask"
import i18n from "@/i18n"
import syncService from "@/services/syncService"
import { TaskStatus } from "@/types/common.type"

// Helper Types/Variables
type Props = {
  syncUuid: string
  fetching: boolean
  offersAllCount: number
  offersIsSellingCount: number
  offersNotSellingCount: number
  offersWithErrorsCount: number
  offersIsNeedActionCount: number
}

const props = defineProps<Props>()
// End Helper Types/Variables

const xmlWithErrors = ref<TaskState>({
  taskId: null,
  progress: 0,
  status: null,
  downloadUrl: null
})

// Composables
const { pollUrlTask } = useTask()
const { downloadFile } = useDownload()
// End Composables

// Computed
const series = computed(() => {
  return [
    props.offersIsSellingCount,
    props.offersNotSellingCount,
    props.offersWithErrorsCount,
    props.offersIsNeedActionCount
  ]
})
const chartOptions = computed(() => {
  return {
    labels: [
      i18n.t("synchronization.sells"),
      i18n.t("synchronization.not_sells"),
      i18n.t("synchronization.xml_errors"),
      i18n.t("synchronization.need_action")
    ],
    colors: ["#52C41A", "#FA8C16", "#F5222D", "#1890FF"],
    plotOptions: {
      pie: {
        expandOnClick: false,
        donut: {
          size: "50%"
        }
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: false
    },
    tooltip: {
      fillSeriesColor: false,
      style: {
        fontSize: "13px",
        fontFamily: "Roboto"
      }
    },
    legend: {
      show: false
    }
  }
})
// End Computed

// Methods
const handleDownloadXmlWithErrors = async (format: "csv" | "xlsx") => {
  const response = await syncService.getXmlWithErrors(props.syncUuid, format)

  xmlWithErrors.value.taskId = response?.task_id

  const url = await pollUrlTask(xmlWithErrors.value.taskId, xmlWithErrors, (taskUuid) =>
    syncService.getReportState({
      synchronizationUuid: props.syncUuid,
      taskUuid
    })
  )

  if (url) {
    downloadFile(url)
  }
}
// End Methods
</script>

<style lang="scss" scoped>
.body {
  display: flex;
  gap: 12px;

  &__apex-wrapper {
    width: 140px;
  }

  &__text-wrapper {
    width: calc(100% - 140px);
    display: grid;
    grid-template-columns: 1fr max-content;
    grid-column-gap: 20px;
    align-content: space-between;
  }

  .text {
    display: contents;

    &__label {
      &--bold {
        font-weight: 500;
      }

      &--dics {
        &::before {
          content: "";
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          margin-right: 10px;
          position: relative;
          top: -2px;
        }
      }

      &--dics-green {
        &::before {
          background-color: #52c41a;
        }
      }

      &--dics-orange {
        &::before {
          background-color: #fa8c16;
        }
      }

      &--dics-red {
        &::before {
          background-color: #f5222d;
        }
      }

      &--dics-blue {
        &::before {
          background-color: $brand-color-6;
        }
      }
    }

    &__value {
      display: flex;
      align-items: center;
      gap: 10px;
    }
  }
}
</style>
